import * as React from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api-client";
import {
  DEADLINE_REVIEW_CTA_LABEL,
  DEADLINE_REVIEW_MAILTO,
  HOLD_INERT_REASON,
} from "@/lib/calibration-deadline";
import { clinicianAdlItems, useAdlItems } from "@/hooks/queries/useAdlItems";
import { useAmpsTasks } from "@/hooks/queries/useAmpsTasks";
import {
  calibrationStatusQueryKey,
  useCalibrationStatus,
} from "@/hooks/queries/useCalibrationStatus";
import {
  CASE_SUBMISSION_STATUS_QUERY_KEY,
  useCaseSubmissionStatus,
} from "@/hooks/queries/useCaseSubmissionStatus";
import { useSubmitCalibrationCase } from "@/hooks/queries/useSubmitCalibrationCase";
import type { CalibrationStatusData } from "@/hooks/queries/calibration-status-types";
import { CalibrationSubNav } from "@/routes/calibration/CalibrationSubNav";
import { CaseVideo } from "@/routes/calibration/CaseVideo";
import { caseAccess, isBoardComplete, nextCase } from "@/routes/calibration/case-access";
import { taskLabel } from "@/routes/calibration/task-label";
import { MAX_UNSCORED_PER_TASK } from "@/routes/evaluation/scoring-validation";
import { ScoreEntryGrid } from "@/routes/evaluation/ScoringStep";

// Story 8.10 (gh-611) — the calibration scoring workspace (UX Screen 17). The last
// missing leaf of the calibration flow: the board's next-case CTA lands here, the
// clinician scores the case's ONE task on the Story 6.5 grid (FR41 — the same entry
// UI as live scoring), submits, and the screen polls the worker to its outcome.
//
// 🚫 The gold-standard endpoint (GET /reference/gold-standard) is clinician-callable
// and returns the faculty's expert per-item scores for this exact case — the ANSWER
// KEY. It also carries a task_code, so it looks like a plausible source. It is never
// called from here: `task_code` comes from the board wire (AC1) and nowhere else.
//
// Deliberate deviations from the mockup (AC9 — do NOT "fix" back):
//   • NO task-tab strip. The mockup's two `score-task-tab`s are a copy artifact from
//     Screen 06 (the task-TABBED evaluation workspace). A calibration case scores
//     exactly ONE task (Story 4.6 Decision #3); the submit wire takes a singular
//     task_code and the reference_data facade raises on a multi-task case.
//   • NO "Save draft" CTA. Same copy artifact — drafts are scoped to EVALUATIONS
//     (PRD FR25/FR26); no calibration draft path exists in any layer and the
//     mockup's button is a dead <a href> back to Screen 16. Logged as deferred work.
//   • `pending` validation pill, not the mockup's `blocked` CSS class — our `blocked`
//     Badge variant means a hard, SETTLED block and pairs with Banner `danger`. An
//     incomplete grid is fixable and must stay below clinical alarm.
//   • AppShell + CalibrationSubNav, not the mockup's standalone sidebar (the 8.7
//     layout reconciliation, inherited by 8.8).
//   • A real player replaces the static "Case video placeholder" text (8.8 precedent).

/**
 * Shared page chrome, so every state renders inside the same host.
 *
 * `heldReason` is threaded straight through to `CalibrationSubNav` (Story 20.2
 * AC10): the Overview passes `HOLD_INERT_REASON` there for a held clinician, and
 * a bare `<CalibrationSubNav />` here would render a LIVE "Case Studies" tab on
 * the workspace while the screen next door renders the same tab inert for the
 * same clinician — the inconsistency reads as the hold being over. A prop rather
 * than a `useCalibrationStatus` call inside the nav for the reason
 * `CalibrationSubNav.tsx` states: three screens mount it, and a self-read would
 * add a query observer to all three.
 */
function ScoringShell({
  children,
  heldReason,
}: {
  children: React.ReactNode;
  heldReason?: string;
}) {
  return (
    <section className="flex flex-col gap-6">
      <CalibrationSubNav heldReason={heldReason} />
      {children}
    </section>
  );
}

function BackToCases({ label = "Back to case studies" }: { label?: string }) {
  return (
    <div>
      <Button asChild variant="secondary">
        <Link to="/calibration/cases">{label}</Link>
      </Button>
    </div>
  );
}

/**
 * Submit-ready predicate — the SAME `scores.length === itemCount` gate the grid
 * renders behind, so the CTA can never enable over an unrendered grid. Before the
 * mount-time hydration effect fills `scores`, a fresh `scores === []` has
 * `missingCount 0` and would otherwise read "ready to submit" with NO grid shown; a
 * fast click there POSTs `scores: []` → CAL-422-INCOMPLETE. Pure + exported so the
 * unit test can pin that exact gap.
 */
export function isReadyToSubmit(itemCount: number, scores: (number | null)[]): boolean {
  return (
    itemCount > 0 &&
    scores.length === itemCount &&
    scores.filter((s) => s === null).length <= MAX_UNSCORED_PER_TASK
  );
}

export function CalibrationCaseScoringScreen() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const statusQuery = useCalibrationStatus({ enabled: true });
  const tasksQuery = useAmpsTasks();
  const adlItemsQuery = useAdlItems();

  const items = React.useMemo(
    () => (adlItemsQuery.data ? clinicianAdlItems(adlItemsQuery.data) : []),
    [adlItemsQuery.data],
  );

  // Local-only entry state — there is NO draft store on this surface (AC9/AC11):
  // calibration is online-only, so nothing is persisted between mounts.
  const [scores, setScores] = React.useState<(number | null)[]>([]);
  React.useEffect(() => {
    setScores((prev) =>
      items.length > 0 && prev.length !== items.length
        ? Array<number | null>(items.length).fill(null)
        : prev,
    );
  }, [items.length]);

  // Flipped once a submission is genuinely in flight server-side — by our own 202,
  // or by CAL-409-DUP telling us one already exists.
  const [awaitingOutcome, setAwaitingOutcome] = React.useState(false);
  // Bumped on every fresh submit so the poll resets its deadline + timed-out latch:
  // a retry after `failed`/timeout keeps `caseId`/`awaitingOutcome` constant, so the
  // poll cannot tell one attempt from the next without an explicit signal (F1).
  const [submissionNonce, setSubmissionNonce] = React.useState(0);
  // Synchronous in-flight latch — see handleSubmit for why state is not enough.
  const inFlightRef = React.useRef(false);
  const submitMutation = useSubmitCalibrationCase(caseId);
  const poll = useCaseSubmissionStatus(caseId, awaitingOutcome, submissionNonce);

  const cases = statusQuery.data?.cases ?? [];
  const access = caseAccess(cases, caseId);
  const caseData = cases.find((c) => c.case_id === caseId);

  const submitCode =
    submitMutation.error instanceof ApiError ? submitMutation.error.code : undefined;
  // P0-CAL-03 twin: a certified / already-calibrated clinician can never submit.
  // Terminal by construction — the CTA is removed, never retried.
  const notInCalibration = submitCode === "CAL-409";
  // Story 20.2 (FR86, AC13) — the day-61 hold, reusing CAL-409's shape exactly:
  // a calm banner and a REMOVED submit CTA, because both are terminal for this
  // request. Terminal, not retryable: nothing the clinician can do on this page
  // lifts a deadline, so a "try again" affordance would be a lie.
  //
  // This is the surface that catches the clinician who deep-links past the inert
  // affordances on the Overview (boundary 11) — so it is the whole of the board's
  // coverage, and the reason the board itself is left untouched.
  //
  // The two codes are adjacent but never interchangeable and the copy says so:
  // CAL-409 is "your calibration is already complete", CALIB-002 is "your window
  // elapsed and a CITS super admin can extend it". Kept as a SEPARATE flag rather
  // than folded into `notInCalibration`, whose name would then be false for half
  // the states it covers.
  //
  // BOTH SOURCES ARE LOAD-BEARING — neither one alone covers the state:
  //
  //  • `deadline_state === "held"` is the only one known BEFORE the first
  //    keystroke. `useCalibrationStatus` is already mounted on this screen (the
  //    board read above), and this route sits deliberately OUTSIDE
  //    `CalibrationGate` (see the comment on it in `app-routes.tsx`), which never
  //    reads `deadline_state` — so without this arm a held clinician who deep
  //    links, or taps Start/Resume on the deliberately-untouched board, gets a
  //    fully live 35-cell workspace, fills it, POSTs, and only then meets the
  //    banner, having lost the entry. It also RELEASES the screen: the hook polls
  //    every 30s while held (L5-1), so a super admin's extension lands here
  //    without a reload.
  //  • `CALIB-002` is still required. `staleTime` is 5 minutes, so a clinician
  //    sitting across the day-60/61 boundary holds a cached `active` payload while
  //    the backend already refuses. Only the submit refusal can see that window.
  const deadlineHeld =
    submitCode === "CALIB-002" || statusQuery.data?.deadline_state === "held";
  const submitBlocked = notInCalibration || deadlineHeld;
  const failedOutcome = poll.status === "failed" || poll.timedOut;
  // Deliberately stays true through the scored → navigate window (the effect's
  // awaited board refetch): the grid must stay read-only and the CTA disabled
  // until the navigate lands — an editable flash there invites a duplicate POST
  // whose removeQueries would cancel the in-flight navigation.
  const polling = awaitingOutcome && !failedOutcome;
  // The grid is read-only for TWO different reasons, and the label below tells
  // them apart because the clinician's question differs: `polling` means the
  // scores were sent, `deadlineHeld` means nothing entered here can be. A banner
  // above 35 LIVE cells still invites exactly the loss it warns about — the hold
  // is terminal for this request, so the entry surface has to be inert too, not
  // merely captioned.
  const gridInert = polling || deadlineHeld;
  // A non-coded submit failure (transport / NETWORK_ERROR, 5xx, an unmapped code)
  // must not vanish — the CTA otherwise flicks back to "ready" with no reason.
  // CAL-409 has its own banner + removed CTA; CAL-409-DUP routes to the poll; the
  // specific CAL-422-*/CAL-404 codes carry their own copy in ValidationState.
  const submitFailed = submitMutation.isError && !submitBlocked;
  // Sustained poll outage after a 202 — a connection note keeps the polling banner
  // honest instead of an inert "Scoring…" for up to the 5-minute timeout. Threshold
  // ≥2 consecutive failures so a single blip can't flicker it (the ResultsStep gate).
  const connectionIssue = poll.failureCount >= 2;

  /**
   * AC5 — the ORDER is load-bearing. The poll reports ONE case, so board completion
   * can only be read off the board; the board is otherwise stale for up to the hook's
   * 5-minute staleTime. Deriving the destination BEFORE the refetch reads the
   * PRE-submit board, so the 5th case never routes to the completion screen.
   *
   * `invalidateQueries` awaits the refetch of ACTIVE queries, and this screen mounts
   * `useCalibrationStatus`, so the read after the await is the fresh board.
   */
  React.useEffect(() => {
    if (poll.status !== "scored") return;
    let cancelled = false;
    void (async () => {
      await queryClient.invalidateQueries({ queryKey: calibrationStatusQueryKey() });
      if (cancelled) return;
      const fresh = queryClient.getQueryData<CalibrationStatusData>(
        calibrationStatusQueryKey(),
      );
      navigate(
        fresh && isBoardComplete(fresh.cases) ? "/calibration/complete" : "/calibration/cases",
        { replace: true },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [poll.status, queryClient, navigate]);

  // AC8 — resume polling for a submission already in flight server-side. After a
  // submit the board reports this case `in_progress` (a queued submission —
  // get_calibration_status D7); if the clinician refreshes / navigates away / taps
  // "Resume case N" before the worker finishes, this workspace remounts with
  // `awaitingOutcome === false` and an idle poll over an empty grid. Without this
  // they'd have to re-enter ≥34 cells just to POST and bounce off CAL-409-DUP to get
  // the poll back — the board already carries the free signal. Gated on `startable`
  // so an out-of-turn `in_progress` case still shows the locked screen; the
  // CAL-409-DUP path stays for the race where the board is momentarily stale.
  React.useEffect(() => {
    if (access === "startable" && caseData?.status === "in_progress") {
      setAwaitingOutcome(true);
    }
  }, [access, caseData?.status]);

  function handleScoreChange(index: number, value: number | null) {
    setScores((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleSubmit() {
    // The `disabled` attribute alone does NOT close the double-click race: both
    // `isPending` and `polling` only reach the DOM on the next render, so two
    // clicks dispatched in the same tick both pass a state-only guard and fire two
    // POSTs — the second landing as CAL-409-DUP. A ref flips synchronously, so the
    // second click is dropped before it can start a request.
    if (inFlightRef.current) return;
    // `submitBlocked` is deliberately the SAME expression that unmounts the CTA
    // below, and it is unreachable today: this handler closes over its own
    // render's value, and any render where `submitBlocked` is true renders no CTA
    // to click — so every invocation that gets here carries `false`. It is
    // therefore NOT what stops the double click (`inFlightRef` above is). It is
    // here so the guard cannot drift away from the render gate if that gate is
    // ever edited, and so a terminal refusal — CAL-409 or CALIB-002, neither of
    // which a retry can clear — can never be re-POSTed from this function.
    if (!caseData || polling || submitMutation.isPending || submitBlocked) return;
    inFlightRef.current = true;
    // Restart the poll for a fresh (or newly discovered) submission. A retry after
    // `failed`/timeout must not read the previous terminal status — the poll would
    // stop immediately on the stale value. `removeQueries` clears the cached outcome
    // so the poll refetches; the nonce bump clears the poll's 5-minute deadline +
    // timed-out latch so the restarted cycle keeps polling (F1 — the two are
    // load-bearing together: remove alone leaves `timedOutRef` latched).
    //
    // Called only AFTER the POST settles (202, or CAL-409-DUP): on a retry the poll
    // is already enabled, and `removeQueries` on an observed query refetches it
    // immediately — done before `mutate`, that GET races the POST and near-always
    // reads the PREVIOUS submission's `failed`, re-latching the poll terminally
    // before the new submission exists (first retry click looks like a failure).
    const restartPoll = () => {
      queryClient.removeQueries({ queryKey: [CASE_SUBMISSION_STATUS_QUERY_KEY, caseId] });
      setSubmissionNonce((n) => n + 1);
      setAwaitingOutcome(true);
    };
    submitMutation.mutate(
      { task_code: caseData.task_code, scores },
      {
        onSuccess: restartPoll,
        onError: (err) => {
          // P0-CAL-04 twin: the submission genuinely exists (double-click, or
          // re-entering a case already submitted), so show the outcome rather than
          // an error — an error here would tell the clinician their work was lost.
          if (err instanceof ApiError && err.code === "CAL-409-DUP") restartPoll();
        },
        // Release the latch once the request settles. A `failed` outcome or a
        // fixable 422 must leave the clinician able to submit again; the `polling`
        // guard covers the accepted path.
        onSettled: () => {
          inFlightRef.current = false;
        },
      },
    );
  }

  // ---- Posture: loading / unreachable ------------------------------------- //

  // AC11 — render nothing while the board read is in flight; never a workspace
  // skeleton that implies a case exists.
  if (statusQuery.isPending) return null;

  if (statusQuery.isError || access === "unknown") {
    return (
      <ScoringShell heldReason={deadlineHeld ? HOLD_INERT_REASON : undefined}>
        <h1 className="display-xs">Calibration case</h1>
        <Banner variant="info" title="Case unavailable">
          <p>
            This calibration case can’t be opened right now. Return to your case studies
            and try again.
          </p>
        </Banner>
        <BackToCases />
      </ScoringShell>
    );
  }

  // A scored case belongs to the read-only log. Redirecting (rather than blocking)
  // is deliberate and different from the locked case below: the destination shows
  // the clinician their OWN work instead of swallowing the intent.
  //
  // But NOT while this screen is itself driving the outcome (`awaitingOutcome`):
  // the scored-effect above owns the post-submit destination (board, or the
  // completion screen on the 5th case — AC5), and its awaited board refetch
  // re-renders this component with `access === "scored"` BEFORE the effect's
  // navigate runs — so an unguarded redirect wins the race, unmounts the effect
  // (cancelling its navigate), and strands the clinician on the log. Real-browser
  // only (W1-caught): jsdom resolves the same race the other way.
  //
  // `failedOutcome` re-opens the redirect: once the poll is terminally silent
  // (`failed`, or the 5-minute timeout latch stops it for good) the scored-effect
  // can never fire — so if the board still learns the case IS scored (focus
  // refetch after a >5-min worker), suppressing the redirect would hold the
  // clinician on a false "couldn't be scored" banner forever. A dead poll cedes
  // the destination back to the log.
  if (access === "scored" && (!awaitingOutcome || failedOutcome)) {
    return <Navigate to={`/calibration/cases/${caseId}/log`} replace />;
  }

  // AC3 — a deep link to an out-of-turn case explains itself and offers the real
  // next case. NEVER a silent redirect: the clinician asked for something specific.
  if (access === "locked") {
    const next = nextCase(cases);
    return (
      <ScoringShell heldReason={deadlineHeld ? HOLD_INERT_REASON : undefined}>
        <h1 className="display-xs">Calibration case {caseData?.order}</h1>
        <Banner variant="info" title={next ? `Complete case ${next.order} first` : "Not available yet"}>
          <p>Calibration cases are scored in order, so this one isn’t open yet.</p>
        </Banner>
        <div className="flex flex-wrap gap-3">
          {next ? (
            <Button asChild variant="primary">
              <Link to={`/calibration/cases/${next.case_id}`}>Go to case {next.order}</Link>
            </Button>
          ) : null}
          <Button asChild variant="secondary">
            <Link to="/calibration/cases">Back to case studies</Link>
          </Button>
        </div>
      </ScoringShell>
    );
  }

  // ---- The workspace ------------------------------------------------------ //

  const label = caseData ? taskLabel(tasksQuery.data, caseData.task_code) : "";
  const missingCount = scores.filter((s) => s === null).length;
  const readyToSubmit = isReadyToSubmit(items.length, scores);

  return (
    <ScoringShell heldReason={deadlineHeld ? HOLD_INERT_REASON : undefined}>
      <div className="flex flex-col gap-2">
        <h1 className="display-xs">Calibration case {caseData?.order}</h1>
        <p className="text-[color:var(--text-secondary)]">
          Review the standardized case material and enter AMPS skill-item scores for the
          preconfigured task below.
        </p>
      </div>

      {notInCalibration ? (
        <Banner variant="info" title="You’re not in calibration">
          <p>
            Your calibration is already complete, so this case can’t be scored. Nothing
            you’ve entered here has been recorded.
          </p>
        </Banner>
      ) : null}

      {/* AC13 — `warn`, never `danger`, and the same desaturated posture as the
          Overview's hold banner: the countdown stopped, it did not fail. */}
      {deadlineHeld ? (
        <Banner variant="warn" role="status" title="Calibration progression is on hold">
          <p>
            Your calibration deadline has passed, so this case can’t be scored right
            now. Nothing you’ve entered here has been recorded, and nothing you had
            already completed has been lost. A CITS super admin can extend your
            deadline or reactivate your account.
          </p>
          <p>
            <a
              className="font-semibold underline underline-offset-4"
              href={DEADLINE_REVIEW_MAILTO}
            >
              {DEADLINE_REVIEW_CTA_LABEL}
            </a>
          </p>
        </Banner>
      ) : null}

      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
          Case context
        </h2>
        <p className="text-sm text-[color:var(--text-tertiary)]">
          Everything needed before scoring begins.
        </p>
        <ul className="flex flex-col gap-2 text-sm text-[color:var(--text-secondary)]">
          <li>
            <strong className="text-[color:var(--text-primary)]">Task:</strong> {label}
          </li>
          <li>
            <strong className="text-[color:var(--text-primary)]">Expected workflow:</strong>{" "}
            observe, score all required items, and submit score
          </li>
        </ul>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">Task data</h2>

        {caseId ? (
          <CaseVideo
            key={caseId}
            caseId={caseId}
            unaffectedNote="Any scores you’ve entered below are unaffected."
          />
        ) : null}

        <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-5">
          <div className="mb-4 flex flex-col gap-1 border-b border-[color:var(--border-primary)] pb-3">
            <strong className="text-sm font-semibold text-[color:var(--text-primary)]">
              Scores
            </strong>
            <span className="text-sm text-[color:var(--text-tertiary)]">{label}</span>
          </div>
          <p className="mb-4 text-sm text-[color:var(--text-secondary)]">
            Enter a score from 1 to 4 for each observed skill item. This calibration case
            follows the same score-entry logic as live AMPS scoring, including the rule
            that no more than 1 item may remain unscored.
          </p>

          {adlItemsQuery.isError ? (
            <Banner variant="info" title="Score list unavailable">
              <p>The skill-item list couldn’t be loaded right now. Try again in a moment.</p>
            </Banner>
          ) : items.length > 0 && scores.length === items.length ? (
            // `taskPosition={1}` is pinned: the grid stamps cell labels as
            // "Task {taskPosition} item {n} …" and every W2 query + E2E helper keys
            // on "^Task 1 item {m} ". Passing the case `order` would silently break
            // all of them. There is one task per case, so 1 is also correct.
            gridInert ? (
              <ScoreEntryGrid
                items={items}
                scores={scores}
                taskPosition={1}
                // The two inert reasons get DIFFERENT names. "(submitted)" is
                // true only of the polling grid — those scores WERE sent. A held
                // grid's were not and cannot be, so reusing the polling label
                // would tell a held clinician their work is in flight.
                gridLabel={
                  polling
                    ? "Scores for the calibration case task (submitted)"
                    : "Scores for the calibration case task (on hold)"
                }
                readOnly
              />
            ) : (
              <ScoreEntryGrid
                items={items}
                scores={scores}
                taskPosition={1}
                gridLabel="Scores for the calibration case task"
                onScoreChange={handleScoreChange}
              />
            )
          ) : null}
        </div>
      </div>

      {/* Silent while the submit is TERMINALLY blocked. The pill's blocked arm
          reads "More items need a score … submit is blocked until only 1 or fewer
          remain unscored" — an instruction to keep typing, rendered directly
          under a banner that has just said the case cannot be scored at all. Both
          blocked states carry their own banner as the reason surface, and neither
          leaves a CTA for a pill to gate, so there is nothing for it to explain. */}
      {submitBlocked ? null : (
        <ValidationState
          missingCount={missingCount}
          ready={readyToSubmit}
          polling={polling}
          failedOutcome={failedOutcome}
          submitCode={submitCode}
          submitFailed={submitFailed}
          connectionIssue={connectionIssue}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="secondary">
          <Link to="/calibration/cases">Back</Link>
        </Button>
        {/* AC9: no "Save draft" — calibration has no draft path in any layer. */}
        {submitBlocked ? null : (
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            // Disabled while a request is in flight, so a double-click cannot race
            // itself into CAL-409-DUP in the first place. `aria-disabled` carries the
            // blocked state to AT; the banner above is the visible REASON (never a
            // silently dead button).
            disabled={!readyToSubmit || polling || submitMutation.isPending}
            aria-disabled={!readyToSubmit || polling || submitMutation.isPending}
          >
            {polling ? "Scoring…" : "Submit score"}
          </Button>
        )}
      </div>
    </ScoringShell>
  );
}

/**
 * AC6/AC8 — the live validation pill + banner, and the server's coded refusals.
 * The banner is the REASON surface: the submit CTA is never disabled without one.
 */
function ValidationState({
  missingCount,
  ready,
  polling,
  failedOutcome,
  submitCode,
  submitFailed,
  connectionIssue,
}: {
  missingCount: number;
  ready: boolean;
  polling: boolean;
  failedOutcome: boolean;
  submitCode: string | undefined;
  submitFailed: boolean;
  connectionIssue: boolean;
}) {
  if (polling) {
    return (
      <div className="flex flex-col gap-3">
        <Badge variant="info">Scoring in progress</Badge>
        <Banner variant="info" title="Scoring your case">
          <p>
            Your scores are in. This usually takes a moment — you’ll be taken back to your
            case studies as soon as it finishes.
          </p>
          {/* M3 — the poll is self-retrying every 2 s; a sustained outage gets an
              honest note rather than an inert "Scoring…" (the ResultsStep precedent). */}
          {connectionIssue ? (
            <p>
              We’re having trouble reaching the server right now — still trying, and your
              scores are safe.
            </p>
          ) : null}
        </Banner>
      </div>
    );
  }

  if (failedOutcome) {
    return (
      <Banner variant="warn" title="Scoring didn’t finish">
        <p>
          This case couldn’t be scored just now. Your scores are still editable below — submit
          the case again to retry.
        </p>
      </Banner>
    );
  }

  // Defense in depth: the grid only emits 1–4 and the 34/35 gate blocks an incomplete
  // submit, so these are unreachable from this UI by construction. A CAL-422-TASK in
  // particular would mean the AC1 board-wire join is wrong.
  if (submitCode === "CAL-422-INCOMPLETE" || submitCode === "CAL-422-RATING") {
    return (
      <Banner variant="warn" title="Scores couldn’t be accepted">
        <p>Check that every item holds a score from 1 to 4, then submit again.</p>
      </Banner>
    );
  }
  if (submitCode === "CAL-422-TASK") {
    return (
      <Banner variant="warn" title="Scores couldn’t be accepted">
        <p>This case expects a different task. Return to your case studies and reopen it.</p>
      </Banner>
    );
  }
  if (submitCode === "CAL-404") {
    return (
      <Banner variant="info" title="Case unavailable">
        <p>This calibration case can’t be found. Return to your case studies.</p>
      </Banner>
    );
  }

  // M2 — a submit that errored with no coded meaning above (transport / 5xx /
  // unmapped code). Calm `warn`, scores kept on screen, CTA still enabled so the
  // clinician can just submit again. Never a silently dead button.
  if (submitFailed) {
    return (
      <Banner variant="warn" title="Submit didn’t go through">
        <p>
          Something interrupted the submission — your scores are still on screen. Check
          your connection and submit the case again.
        </p>
      </Banner>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* `pending`, not `blocked` — see the deviation note in the module header. */}
      <Badge variant={ready ? "ok" : "pending"}>
        {ready
          ? "34/35 required - ready to submit"
          : `34/35 required - ${missingCount} items missing`}
      </Badge>
      {ready ? (
        <Banner variant="success" title="Ready to submit">
          <p>Max 1 item may remain unscored (34/35 required).</p>
        </Banner>
      ) : (
        <Banner variant="warn" title="More items need a score">
          <p>
            Currently {missingCount} items are missing - submit is blocked until only 1 or
            fewer remain unscored.
          </p>
        </Banner>
      )}
    </div>
  );
}

export default CalibrationCaseScoringScreen;
